import React from "react";
//#region redux
import { useSelector } from "react-redux";
import { AppState } from "store";
//#endregion
//#region types
import { CurrencyEnums, DimensionEnums, CommonEnums } from "models/enums";
import { FormFieldConstants, IconDefinitionConstants } from "utils/constants";
import { UploadedFile } from "models/utilTypes/fileUtilsTypes";
import { TranslationKeys } from "translations";
import { DocumentFormTypes, ReceiptFormTypes } from "models/formTypes";
import { WintSuperkollAPIModel } from "wint-api-client/dist/types";
import { AutocompleteListItem } from "models/propTypes/listPropTypes";
import { FormikProps } from "formik";
import { SelectionItem } from "models/utilTypes/selectionTypes";
import { ArraySchema } from "models/utilTypes/yupSchemaTypes";
//#endregion
//#region components
import { useCheckRelations, useDimensionMaps } from "components/common";
import { AddPaymentMethodFormContainer, useReceiptCategories } from "components/receipt";
import { useReceiptPaymentMethodService } from "serviceHooks";
import useSelfInvoicePermissions from "components/selfInvoice/useSelfInvoicePermissions";
import { useIncomingInvoicePermissions } from "components/incomingInvoice";
//#endregion
import * as Yup from "yup";
import { LocalizationHelperInstance, RegularExpressions, UtilsHelper } from "utils/helpers";
import { OTHER_PAYMENT_METHOD_ID } from "utils/constants/receiptConstants";

function useCreateFormHelpers(
  disableDefaultSelection?: boolean,
  isDocumentDraft?: boolean,
  documentDraftCreated?: Date,
  documentDraftAttachments?: UploadedFile[]
) {
  const receiptPaymentMethodService = useReceiptPaymentMethodService();
  const { canCreateSelfInvoices } = useSelfInvoicePermissions();

  const [dimensionAmount, setDimensionAmount] = React.useState<number>(0);
  const [currency, setCurrency] = React.useState<string>(CurrencyEnums.CurrencyCode.SEK);
  const [topReceiptPaymentMethods, setTopReceiptPaymentMethods] = React.useState<AutocompleteListItem[]>([]);
  const [isReceiptLoading, setIsReceiptLoading] = React.useState(true);

  const { currentUser, selectedCompany } = useSelector(
    (store: AppState) => ({
      currentUser: store.auth.loggedInUser.name,
      selectedCompany: store.auth.selectedCompany,
    }),
    (left, right) => left.currentUser === right.currentUser && left.selectedCompany === right.selectedCompany
  );

  const storeReceiptPaymentMethods = useSelector(
    (x: AppState) => x.auth.loggedInUser.paymentMethods,
    (left, right) => left.map((l) => l.id).join("") === right.map((r) => r.id).join("")
  );

  const { canViewIncomingInvoices } = useIncomingInvoicePermissions();
  const { getIsAdmin } = useCheckRelations();
  const isAdmin = getIsAdmin();

  const availableDocumentTypes: SelectionItem[] = React.useMemo(() => {
    //#region set document types
    const documentTypes: CommonEnums.DocumentTypes[] = [];

    if (selectedCompany.receiptCreator) documentTypes.push(CommonEnums.DocumentTypes.Receipt);
    if (selectedCompany.supplierInvoiceCreator && canViewIncomingInvoices) {
      documentTypes.push(CommonEnums.DocumentTypes.IncomingInvoice);
    }
    if (canCreateSelfInvoices() && !isDocumentDraft) {
      documentTypes.push(CommonEnums.DocumentTypes.SelfInvoice);
    }
    if (selectedCompany.genericDocumentCreator) {
      documentTypes.push(CommonEnums.DocumentTypes.GenericDocument);
    }
    if (!isDocumentDraft && (isAdmin || selectedCompany.genericDocumentCreator)) {
      documentTypes.push(CommonEnums.DocumentTypes.SIEFiles);
    }

    return documentTypes.map((type) => ({
      id: type,
      value: LocalizationHelperInstance.getTranslatedEnumValue("Document.Types", type),
    }));
    //#endregion
  }, [selectedCompany, isDocumentDraft]);

  const {
    receiptCategoriesSelectItems: receiptCategoriesInputItems,
    receiptCategoriesAutoCompleteItems: receiptCategories,
  } = useReceiptCategories();

  const receiptPaymentMethods: AutocompleteListItem[] = React.useMemo(() => {
    return (
      storeReceiptPaymentMethods?.map((p) => ({
        id: p.id,
        value: p.name ?? "",
        group: LocalizationHelperInstance.getFormattedMessage(TranslationKeys.Common.All),
      })) ?? []
    );
  }, [storeReceiptPaymentMethods]);

  const canPayEmployee = useSelector((store: AppState) => store.auth.canPayEmployee);

  const { personNumber, bankAccount, bankClearing, hasMissingPaymentDetails } = useSelector((x: AppState) => ({
    personNumber: x.auth.loggedInUser.personNumber,
    bankAccount: x.auth.loggedInUser.receiptBankAccount,
    bankClearing: x.auth.loggedInUser.receiptBankClearing,
    hasMissingPaymentDetails: x.auth.loggedInUser.hasMissingPaymentDetails,
  }));

  React.useEffect(() => {
    if (selectedCompany.receiptCreator)
      receiptPaymentMethodService.getTopPaymentMethods().then((res) => {
        setTopReceiptPaymentMethods(
          res?.map((p) => ({
            id: p.id,
            value: p.name ?? "",
            group: LocalizationHelperInstance.getFormattedMessage(TranslationKeys.Common.Suggestions),
          })) ?? []
        );
      });
  }, []);

  const receiptPaymentMethodActions = React.useMemo(
    (): AutocompleteListItem[] => [
      {
        value: LocalizationHelperInstance.getFormattedMessage(TranslationKeys.Receipt.PaymentMethod),
        title: LocalizationHelperInstance.getFormattedMessage(TranslationKeys.Common.EditPayment),
        subtitle: LocalizationHelperInstance.getFormattedMessage(TranslationKeys.Common.PaymentSettings),
        icon: IconDefinitionConstants.WPlus,
        form: (
          <AddPaymentMethodFormContainer
            submitResultFormatter={(paymentMethod: WintSuperkollAPIModel.ReceiptPaymentMethod) => ({
              id: paymentMethod.id,
              value: paymentMethod.name,
              data: paymentMethod,
            })}
          />
        ),
        formIcon: IconDefinitionConstants.WIncome2,
        group: " ",
      },
    ],
    []
  );

  const { dimensions, forcedDimensionMap } = useDimensionMaps(DimensionEnums.DimensionCategory.Receipt);

  const hasDimensions = dimensions && dimensions.length > 0;

  const onAmountChange = (name: string, updatedValue: number) => {
    setDimensionAmount(updatedValue);
  };

  const onCurrencyChange = (name: string, updatedValue: string) => {
    setCurrency(updatedValue);
  };

  const onReceiptCategorySelect = (
    item: AutocompleteListItem,
    name: string,
    formikProps: FormikProps<DocumentFormTypes.Create>
  ) => {
    const { setFieldValue } = formikProps;
    setFieldValue(
      FormFieldConstants.Document.Create.ReceiptCategoryType,
      (item.data as WintSuperkollAPIModel.ReceiptCategory).categoryType
    );
  };

  const onReceiptCategoryClear = (name: string, formikProps: FormikProps<DocumentFormTypes.Create>) => {
    const { setFieldValue } = formikProps;
    setFieldValue(FormFieldConstants.Document.Create.ReceiptCategoryType, undefined);
  };

  const initialValues = React.useMemo<DocumentFormTypes.CreateNew>(() => {
    return {
      type: disableDefaultSelection ? undefined : (availableDocumentTypes[0].id as CommonEnums.DocumentTypes),
      receipt: {
        date: isDocumentDraft ? documentDraftCreated : new Date(),
        currency: CurrencyEnums.CurrencyCode.SEK,
        purpose: LocalizationHelperInstance.getFormattedMessage(TranslationKeys.Receipt.DefaultRepresentationPurpose),
        canBePayedOut: canPayEmployee != null ? canPayEmployee! : false,
        personNumber: personNumber?.replace("-", ""),
        bankAccount: bankAccount,
        clearingNumber: bankClearing,
        persons: [
          {
            id: UtilsHelper.getUniqueId(),
            name: currentUser,
            company: selectedCompany.name ?? "",
          },
        ],
        isManualPayment: hasMissingPaymentDetails,
        hasDimensions: hasDimensions,
        internalNote: "",
      },
      incomingInvoice: {},
      document: { bookingDate: new Date() },
      isDocumentDraft: isDocumentDraft,
      filesToUpload: documentDraftAttachments,
    };
  }, [hasDimensions, isDocumentDraft]);

  const validationSchema = React.useMemo(
    () =>
      Yup.object<DocumentFormTypes.CreateNew>({
        type: Yup.number().required(TranslationKeys.Validations.Required),
        filesToUpload: Yup.array()
          .of(
            Yup.object<UploadedFile>({
              contentType: Yup.string(),
              fileName: Yup.string(),
              fileSize: Yup.number(),
              data: Yup.string(),
            })
          )
          .min(1, TranslationKeys.Validations.Required)
          .required(TranslationKeys.Validations.Required),
        receipt: Yup.object<DocumentFormTypes.CreateReceipt>().when(
          FormFieldConstants.Document.Create.Type,
          ([type], schema: Yup.ObjectSchema<{}, DocumentFormTypes.CreateReceipt>) =>
            type === WintSuperkollAPIModel.DocumentDraftType.Receipt
              ? schema.shape({
                  date: Yup.date()
                    .typeError(TranslationKeys.Validations.InvalidDate)
                    .required(TranslationKeys.Validations.Required),
                  amount: Yup.number()
                    .required(TranslationKeys.Validations.Required)
                    .min(0.01, TranslationKeys.Validations.Required),
                  currency: Yup.string().required(TranslationKeys.Validations.Required),
                  category: Yup.number().required(TranslationKeys.Validations.Required),
                  paymentMethod: Yup.number().required(TranslationKeys.Validations.Required),
                  purpose: Yup.string().when(
                    [FormFieldConstants.Document.CreateReceipt.Category],
                    ([receiptCategory], schema: Yup.StringSchema) =>
                      ((receiptCategory === WintSuperkollAPIModel.ReceiptCategoryType.Representation ||
                        receiptCategory === WintSuperkollAPIModel.ReceiptCategoryType.EmployeeRepresentation) &&
                        schema.required(TranslationKeys.Validations.Required)) ||
                      schema
                  ),
                  otherExplanation: Yup.string().when(
                    [FormFieldConstants.Document.CreateReceipt.Category],
                    ([receiptCategory], schema: Yup.StringSchema) =>
                      (receiptCategory === WintSuperkollAPIModel.ReceiptCategoryType.Other &&
                        schema.required(TranslationKeys.Validations.Required)) ||
                      schema
                  ),
                  otherPaymentMethodExplanation: Yup.string().when(
                    [FormFieldConstants.Document.CreateReceipt.PaymentMethod],
                    ([paymentMethod], schema: Yup.StringSchema) =>
                      paymentMethod === OTHER_PAYMENT_METHOD_ID
                        ? schema.required(TranslationKeys.Validations.Required)
                        : schema
                  ),
                  internalNote: Yup.string(),
                  persons: Yup.array()
                    .of(
                      Yup.object<ReceiptFormTypes.ReceiptPerson>({
                        id: Yup.number(),
                        name: Yup.string().required(TranslationKeys.Validations.Required),
                        company: Yup.string().required(TranslationKeys.Validations.Required),
                      })
                    )
                    .when(
                      [FormFieldConstants.Document.Create.ReceiptCategoryType],
                      (
                        [receiptCategory]: [WintSuperkollAPIModel.ReceiptCategoryType],
                        schema: ArraySchema<ReceiptFormTypes.ReceiptPerson>
                      ) =>
                        ((receiptCategory === WintSuperkollAPIModel.ReceiptCategoryType.Representation ||
                          receiptCategory === WintSuperkollAPIModel.ReceiptCategoryType.EmployeeRepresentation) &&
                          schema.required(TranslationKeys.Validations.Required)) ||
                        schema
                    ),
                  canBePayedOut: Yup.boolean(),
                  isManualPayment: Yup.boolean(),
                  personNumber: Yup.string()
                    .matches(RegularExpressions.PersonNumber, {
                      message: TranslationKeys.Validations.InvalidPersonNumber,
                      excludeEmptyString: true,
                    })
                    .when(
                      [
                        FormFieldConstants.Document.CreateReceipt.IsManualPayment,
                        FormFieldConstants.Document.CreateReceipt.CanBePayedOut,
                      ],
                      ([isReceiptManualPayment, canBePayedOut], schema: Yup.StringSchema) =>
                        !isReceiptManualPayment && !canBePayedOut
                          ? schema.required(TranslationKeys.Validations.Required)
                          : schema
                    ),
                  bankAccount: Yup.string().when(
                    [
                      FormFieldConstants.Document.CreateReceipt.IsManualPayment,
                      FormFieldConstants.Document.CreateReceipt.CanBePayedOut,
                    ],
                    ([isReceiptManualPayment, canBePayedOut], schema: Yup.StringSchema) =>
                      !isReceiptManualPayment && !canBePayedOut
                        ? schema.required(TranslationKeys.Validations.Required)
                        : schema
                  ),
                  clearingNumber: Yup.string().when(
                    [
                      FormFieldConstants.Document.CreateReceipt.IsManualPayment,
                      FormFieldConstants.Document.CreateReceipt.CanBePayedOut,
                    ],
                    ([isReceiptManualPayment, canBePayedOut], schema: Yup.StringSchema) =>
                      !isReceiptManualPayment && !canBePayedOut
                        ? schema.required(TranslationKeys.Validations.Required)
                        : schema
                  ),
                  dimensions: Yup.array().when(
                    [FormFieldConstants.Document.CreateReceipt.Amount],
                    (
                      [amount]: [number],
                      schema: Yup.ArraySchema<
                        WintSuperkollAPIModel.DimensionData[],
                        any,
                        undefined,
                        "",
                        WintSuperkollAPIModel.DimensionData
                      >
                    ) =>
                      schema
                        .of(
                          Yup.object<WintSuperkollAPIModel.DimensionData>({
                            dimensionId: Yup.string(),
                            amount: Yup.number(),
                          })
                        )
                        .checkForcedDimensions(forcedDimensionMap)
                        .validateDimensionAmounts(dimensions, amount)
                  ),
                  hasDimensions: Yup.boolean(),
                })
              : schema
        ),
        incomingInvoice: Yup.object<DocumentFormTypes.CreateIncomingInvoice>({}),
        document: Yup.object<DocumentFormTypes.CreateGenericDocument>({
          subject: Yup.string(),
          bookingDate: Yup.date().typeError(TranslationKeys.Validations.InvalidDate),
        }),
      }),
    [dimensionAmount, dimensions, forcedDimensionMap, hasDimensions]
  );

  return {
    initialValues,
    validationSchema,
    onReceiptCategorySelect,
    onReceiptCategoryClear,
    onAmountChange,
    onCurrencyChange,
    availableDocumentTypes,
    receiptCategories,
    receiptPaymentMethods,
    topReceiptPaymentMethods,
    receiptCategoriesInputItems,
    receiptPaymentMethodActions,
    dimensionAmount,
    currency,
    isReceiptLoading: selectedCompany.receiptCreator && isReceiptLoading,
    setIsReceiptLoading,
  };
}

export default useCreateFormHelpers;
